import React, { useState, useEffect } from 'react';
import { login, register, requestPasswordReset, resetPassword } from '../services/api';

function AuthModal({ isOpen, onClose, onLoginSuccess, redirectAfterLogin = false, redirectAfterRegister = false }) {
  const [activeTab, setActiveTab] = useState('signin'); // 'signin' or 'register'
  const [showPasswordReset, setShowPasswordReset] = useState(false); // Show password reset form
  const [resetStep, setResetStep] = useState('request'); // 'request' or 'reset'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Password visibility states
  const [showSignInPassword, setShowSignInPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showResetConfirmPassword, setShowResetConfirmPassword] = useState(false);
  
  // Form states
  const [signInForm, setSignInForm] = useState({
    email: '',
    password: '',
  });
  
  const [registerForm, setRegisterForm] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    password_confirmation: '',
  });

  const [resetForm, setResetForm] = useState({
    email: '',
    otp: '',
    password: '',
    password_confirmation: '',
  });

  // Reset form when modal opens/closes or tab changes
  useEffect(() => {
    if (isOpen) {
      setError('');
      setSuccess('');
      setShowPasswordReset(false);
      setResetStep('request');
      setSignInForm({ email: '', password: '' });
      setRegisterForm({ name: '', email: '', phone: '', password: '', password_confirmation: '' });
      setResetForm({ email: '', otp: '', password: '', password_confirmation: '' });
      setShowSignInPassword(false);
      setShowRegisterPassword(false);
      setShowConfirmPassword(false);
      setShowResetPassword(false);
      setShowResetConfirmPassword(false);
    }
  }, [isOpen, activeTab]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSignInChange = (e) => {
    setSignInForm({
      ...signInForm,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  // Phone: digits only, max 10
  const sanitizePhone = (value) => value.replace(/\D/g, '').slice(0, 10);

  const handleRegisterChange = (e) => {
    const { name, value } = e.target;
    const next = { ...registerForm, [name]: value };
    if (name === 'phone') {
      next.phone = sanitizePhone(value);
    }
    setRegisterForm(next);
    setError('');
  };

  const handleResetChange = (e) => {
    setResetForm({
      ...resetForm,
      [e.target.name]: e.target.value,
    });
    setError('');
    setSuccess('');
  };

  const handleRequestPasswordReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await requestPasswordReset(resetForm.email);
      setSuccess(response.message || 'Password reset OTP has been sent to your mobile number.');
      // Move to reset step to enter OTP
      setResetStep('reset');
    } catch (err) {
      setError(err.message || 'Failed to request password reset. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // Validate passwords match
    if (resetForm.password !== resetForm.password_confirmation) {
      setError('Passwords do not match.');
      setLoading(false);
      return;
    }

    // Validate password length
    if (resetForm.password.length < 8) {
      setError('Password must be at least 8 characters long.');
      setLoading(false);
      return;
    }

    // Validate OTP format (6 digits)
    if (!/^\d{6}$/.test(resetForm.otp)) {
      setError('OTP must be exactly 6 digits.');
      setLoading(false);
      return;
    }

    try {
      const response = await resetPassword(
        resetForm.email,
        resetForm.otp,
        resetForm.password,
        resetForm.password_confirmation
      );
      setSuccess(response.message || 'Password has been reset successfully. You can now sign in.');
      setTimeout(() => {
        setShowPasswordReset(false);
        setResetStep('request');
        setResetForm({ email: '', otp: '', password: '', password_confirmation: '' });
        setActiveTab('signin');
      }, 2000);
    } catch (err) {
      setError(err.message || 'Failed to reset password. Please check your OTP and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await login(signInForm.email, signInForm.password);
      // Store token in localStorage
      localStorage.setItem('auth_token', response.token);
      localStorage.setItem('user', JSON.stringify(response.user));
      
      if (onLoginSuccess) {
        onLoginSuccess(response.user, response.token, { redirectAfterLogin });
      }
      onClose();
    } catch (err) {
      setError(err.message || 'Invalid email or password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate passwords match
    if (registerForm.password !== registerForm.password_confirmation) {
      setError('Passwords do not match.');
      setLoading(false);
      return;
    }

    // Validate password length
    if (registerForm.password.length < 8) {
      setError('Password must be at least 8 characters long.');
      setLoading(false);
      return;
    }

    try {
      const response = await register(
        registerForm.name,
        registerForm.email,
        registerForm.password,
        registerForm.password_confirmation,
        registerForm.phone || null
      );
      // Store token in localStorage
      localStorage.setItem('auth_token', response.token);
      localStorage.setItem('user', JSON.stringify(response.user));
      
      if (onLoginSuccess) {
        onLoginSuccess(response.user, response.token, { redirectAfterRegister });
      }
      onClose();
    } catch (err) {
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 z-[80] transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 overflow-y-auto">
        <div 
          className="bg-white rounded-lg shadow-2xl max-w-md w-full relative animate-modal-pop"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full transition-colors z-10"
            aria-label="Close modal"
          >
            <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <div className="p-6 pt-8">
            {/* Tabs - Hide when password reset is shown */}
            {!showPasswordReset && (
              <div className="flex border-b border-gray-200 mb-6">
              <button
                onClick={() => setActiveTab('signin')}
                className={`flex-1 py-3 text-center font-semibold transition-colors ${
                  activeTab === 'signin'
                    ? 'text-primary border-b-2 border-primary'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Sign In
              </button>
              <button
                onClick={() => setActiveTab('register')}
                className={`flex-1 py-3 text-center font-semibold transition-colors ${
                  activeTab === 'register'
                    ? 'text-primary border-b-2 border-primary'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Register
              </button>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {/* Success Message */}
            {success && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-600">{success}</p>
              </div>
            )}

            {/* Password Reset Form */}
            {showPasswordReset && (
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {resetStep === 'request' ? 'Reset Password' : 'Enter New Password'}
                  </h3>
                  <button
                    onClick={() => {
                      setShowPasswordReset(false);
                      setResetStep('request');
                      setResetForm({ email: '', otp: '', password: '', password_confirmation: '' });
                      setError('');
                      setSuccess('');
                    }}
                    className="text-sm text-gray-500 hover:text-gray-700"
                  >
                    Back to Sign In
                  </button>
                </div>

                {resetStep === 'request' ? (
                  <form onSubmit={handleRequestPasswordReset} className="space-y-4">
                    <div>
                      <label htmlFor="reset-email" className="block text-sm font-medium text-gray-700 mb-1">
                        Email
                      </label>
                      <input
                        type="email"
                        id="reset-email"
                        name="email"
                        value={resetForm.email}
                        onChange={handleResetChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/10 focus:border-primary"
                        placeholder="your@email.com"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-primary text-white py-3 rounded-lg font-semibold transition-all hover:bg-primary-dark hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? 'Sending OTP...' : 'Send OTP'}
                    </button>
                  </form>
                ) : (
                  <form onSubmit={handleResetPassword} className="space-y-4">
                    <div>
                      <label htmlFor="reset-otp" className="block text-sm font-medium text-gray-700 mb-1">
                        OTP Code
                      </label>
                      <input
                        type="text"
                        id="reset-otp"
                        name="otp"
                        value={resetForm.otp}
                        onChange={handleResetChange}
                        required
                        maxLength={6}
                        pattern="[0-9]{6}"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/10 focus:border-primary text-center text-2xl tracking-widest"
                        placeholder="000000"
                      />
                      <p className="text-xs text-gray-500 mt-1">Enter the 6-digit OTP sent to your mobile number</p>
                    </div>
                    <div>
                      <label htmlFor="reset-password" className="block text-sm font-medium text-gray-700 mb-1">
                        New Password
                      </label>
                      <div className="relative">
                        <input
                          type={showResetPassword ? 'text' : 'password'}
                          id="reset-password"
                          name="password"
                          value={resetForm.password}
                          onChange={handleResetChange}
                          required
                          minLength={8}
                          className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/10 focus:border-primary"
                          placeholder="Enter new password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowResetPassword(!showResetPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                        >
                          {showResetPassword ? '👁️' : '👁️‍🗨️'}
                        </button>
                      </div>
                    </div>
                    <div>
                      <label htmlFor="reset-password-confirm" className="block text-sm font-medium text-gray-700 mb-1">
                        Confirm New Password
                      </label>
                      <div className="relative">
                        <input
                          type={showResetConfirmPassword ? 'text' : 'password'}
                          id="reset-password-confirm"
                          name="password_confirmation"
                          value={resetForm.password_confirmation}
                          onChange={handleResetChange}
                          required
                          minLength={8}
                          className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/10 focus:border-primary"
                          placeholder="Confirm new password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowResetConfirmPassword(!showResetConfirmPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                        >
                          {showResetConfirmPassword ? '👁️' : '👁️‍🗨️'}
                        </button>
                      </div>
                    </div>
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-primary text-white py-3 rounded-lg font-semibold transition-all hover:bg-primary-dark hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? 'Resetting...' : 'Reset Password'}
                    </button>
                  </form>
                )}
              </div>
            )}

            {/* Sign In Form */}
            {!showPasswordReset && activeTab === 'signin' && (
              <form onSubmit={handleSignIn} className="space-y-4">
                <div>
                  <label htmlFor="signin-email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    id="signin-email"
                    name="email"
                    value={signInForm.email}
                    onChange={handleSignInChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/10 focus:border-primary"
                    placeholder="your@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="signin-password" className="block text-sm font-medium text-gray-700 mb-1">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      type={showSignInPassword ? 'text' : 'password'}
                      id="signin-password"
                      name="password"
                      value={signInForm.password}
                      onChange={handleSignInChange}
                      required
                      className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/10 focus:border-primary"
                      placeholder="Enter your password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowSignInPassword(!showSignInPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                      aria-label={showSignInPassword ? 'Hide password' : 'Show password'}
                    >
                      {showSignInPassword ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.88l1.5 1.5M15.818 8.182l1.5 1.5M11.182 13.818l1.5 1.5M14.121 15.12l1.5 1.5M6.657 17.343l1.5 1.5M4.929 4.929a9.97 9.97 0 011.563-3.029M19.071 19.071a10.05 10.05 0 01-7.196 2.929M19.071 19.071L4.929 4.929" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-end">
                  <button
                    type="button"
                    onClick={() => {
                      setShowPasswordReset(true);
                      setResetForm({ ...resetForm, email: signInForm.email });
                      setResetStep('request');
                      setError('');
                      setSuccess('');
                    }}
                    className="text-sm text-primary hover:text-primary-dark font-medium"
                  >
                    Forgot Password?
                  </button>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-primary text-white py-3 rounded-lg font-semibold transition-all hover:bg-primary-dark hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Signing in...' : 'Sign In'}
                </button>
              </form>
            )}

            {/* Register Form */}
            {activeTab === 'register' && (
              <form onSubmit={handleRegister} className="space-y-4">
                <div>
                  <label htmlFor="register-name" className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name
                  </label>
                  <input
                    type="text"
                    id="register-name"
                    name="name"
                    value={registerForm.name}
                    onChange={handleRegisterChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/10 focus:border-primary"
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <label htmlFor="register-email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    id="register-email"
                    name="email"
                    value={registerForm.email}
                    onChange={handleRegisterChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/10 focus:border-primary"
                    placeholder="your@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="register-phone" className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="register-phone"
                    name="phone"
                    value={registerForm.phone}
                    onChange={handleRegisterChange}
                    maxLength={10}
                    inputMode="numeric"
                    autoComplete="tel"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/10 focus:border-primary"
                    placeholder="e.g. 0249000451"
                  />
                  <p className="mt-1 text-xs text-gray-500">Numbers only, max 10 digits</p>
                </div>

                <div>
                  <label htmlFor="register-password" className="block text-sm font-medium text-gray-700 mb-1">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      type={showRegisterPassword ? 'text' : 'password'}
                      id="register-password"
                      name="password"
                      value={registerForm.password}
                      onChange={handleRegisterChange}
                      required
                      minLength={8}
                      className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/10 focus:border-primary"
                      placeholder="At least 8 characters"
                    />
                    <button
                      type="button"
                      onClick={() => setShowRegisterPassword(!showRegisterPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                      aria-label={showRegisterPassword ? 'Hide password' : 'Show password'}
                    >
                      {showRegisterPassword ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.88l1.5 1.5M15.818 8.182l1.5 1.5M11.182 13.818l1.5 1.5M14.121 15.12l1.5 1.5M6.657 17.343l1.5 1.5M4.929 4.929a9.97 9.97 0 011.563-3.029M19.071 19.071a10.05 10.05 0 01-7.196 2.929M19.071 19.071L4.929 4.929" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <label htmlFor="register-password-confirm" className="block text-sm font-medium text-gray-700 mb-1">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      id="register-password-confirm"
                      name="password_confirmation"
                      value={registerForm.password_confirmation}
                      onChange={handleRegisterChange}
                      required
                      minLength={8}
                      className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/10 focus:border-primary"
                      placeholder="Confirm your password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                      aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                    >
                      {showConfirmPassword ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.88l1.5 1.5M15.818 8.182l1.5 1.5M11.182 13.818l1.5 1.5M14.121 15.12l1.5 1.5M6.657 17.343l1.5 1.5M4.929 4.929a9.97 9.97 0 011.563-3.029M19.071 19.071a10.05 10.05 0 01-7.196 2.929M19.071 19.071L4.929 4.929" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-primary text-white py-3 rounded-lg font-semibold transition-all hover:bg-primary-dark hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Creating account...' : 'Create Account'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default AuthModal;

